/**
 * useRadiantThemeController — single owner of runtime radiant theming.
 *
 * Consolidates what used to be three separate effects in
 * authenticated-app-layout.tsx (initial brand/mode apply, the
 * data-radiant-theme / nav-bridge reaction, and the appearance-mode +
 * matchMedia listener). Having one owner removes the class of bug where the
 * effects set data-theme / radiantNavMode / brand tokens from different
 * places and disagreed about ordering.
 *
 * Responsibilities:
 *   - Flag OFF  → fully inert (no data-theme, no brand, nav bridge off).
 *   - Flag ON   → resolve the persisted brand color + the user's appearance
 *                 mode (default 'light'), set data-theme, inject brand
 *                 tokens, and keep them in sync with:
 *                   • the appearance dropdown  ('rd:user-theme-changed')
 *                   • the OS scheme            (matchMedia, when mode=system)
 *                   • the admin brand picker   (radiant-theme:apply/clear/nav)
 *   - Embedded  → the mode comes only from the host: the SDK's isDarkMode,
 *                 then the UpdateTheme host event while the embed is open.
 *
 * Source of truth for the ACTIVE brand color is getCurrentSourceColor()
 * (runtime-applied, reflects an admin change made this session) with the
 * persisted config value as the reload fallback. Re-running on the persisted
 * value means a late-arriving config still themes correctly.
 */
import { blinkConstants } from '@thoughtspot/blink-constants';
import { Subscriber } from '@thoughtspot/blink-context';
import {
    resolveThemeMode,
    subscribeToConfigChange,
} from '@thoughtspot/custom-style-service';
import { IncomingEventType, UpdateThemeParams } from '@thoughtspot/embed-util';
import { useEffect, useMemo, useRef, useState } from 'react';
import { setAppThemeCssVariables } from '/@utils/embed.util';
import {
    applyTheme,
    getCurrentSourceColor,
    isNavColorLiteral,
} from './dynamic-theme/apply-theme';
import { registerDynamicThemeListener } from './dynamic-theme/global-api';
import {
    activateNavThemeBridge,
    deactivateNavThemeBridge,
} from './dynamic-theme/nav-theme-bridge';
import {
    getUserThemeModePref,
    resolveEffectiveDark,
    UserThemeMode,
} from './user-theme-preference';

/** Minimal shape of the services the controller consumes. */
interface CustomStylingConfigLike {
    getRawAppPanelColor?: () => string | null;
    getApplyAsTopNavColor?: () => boolean;
    hasCustomAxisTitleColor?: () => boolean;
}
interface CustomStylingServiceLike {
    getConfig: () => CustomStylingConfigLike | null;
}

/**
 * --ts-var-* leaves the brand theme must not claim: an admin style setting
 * reaches them through a `var(--ts-var-x, <adminValue>)` fallback that only
 * fires while the var is unset. Empty unless such an admin value exists, so a
 * cluster without one is unaffected.
 */
function getCascadeBreakVars(config: CustomStylingConfigLike | null): string[] {
    return config?.hasCustomAxisTitleColor?.()
        ? ['--ts-var-axis-title-color']
        : [];
}
interface SessionServiceLike {
    getUserGuid?: () => string;
}

export interface RadiantThemeControllerParams {
    radiantEnabled: boolean;
    customStylingService: CustomStylingServiceLike;
    sessionService: SessionServiceLike | null | undefined;
    /**
     * Legacy theme-type used when radiant nav mode is inactive. Also serves as
     * a "config loaded" signal — it flips to CUSTOM once a brand color is
     * present, which re-derives the persisted brand color below.
     */
    themeType: string | undefined;
    /** Theme type to fall back to when radiant nav mode is null. */
    leftNavThemeType: string | undefined;
    isEmbedded?: boolean;
    isDarkModeInEmbed?: boolean;
    /** Embed host-event subscriber; a no-op outside an embed. */
    useEmbedEventSubscription?: Subscriber;
    /** Reports an invalid host event to the host; a no-op outside an embed. */
    dispatchValidationError?: (errorMessage: string) => void;
}

export interface RadiantThemeControllerResult {
    /** 'light' | 'dark' | 'brand', or null when the radiant flow is inactive. */
    radiantNavMode: string | null;
    /** Legacy themeType constant mapped from the active radiant nav mode. */
    effectiveThemeType: string | undefined;
}

/**
 * Push applied vars into the embed CSS infra (so iframes / SDK see them).
 *
 * These go to their own <style> element, NOT through addCustomCssElements:
 * that is the embed customer's channel and it replaces both embed CSS stores
 * and rebuilds the customer's <style> from only the variables handed to it, so
 * echoing theme vars through it wiped every customizations.style override.
 */
function registerAppliedVars(vars: Record<string, string>): void {
    if (Object.keys(vars).length === 0) return;
    setAppThemeCssVariables(vars);
}

/** Default for the embed-only params outside an embed. */
const noop = (): void => undefined;

export function useRadiantThemeController({
    radiantEnabled,
    customStylingService,
    sessionService,
    themeType,
    leftNavThemeType,
    isEmbedded = false,
    isDarkModeInEmbed = false,
    useEmbedEventSubscription = noop,
    dispatchValidationError = noop,
}: RadiantThemeControllerParams): RadiantThemeControllerResult {
    const [radiantNavMode, setRadiantNavMode] = useState<string | null>(null);
    // Brand hex the top nav is painted with literally, or null for the
    // generated surface. A ref, not state: it is only read to write CSS
    // variables, and re-rendering here re-creates the routed content element,
    // which <Content shouldScrollOnChildrenChange> reads as a page change.
    const navBaseColorRef = useRef<string | null>(null);

    /**
     * Repaint the nav. Toggling the base color leaves radiantNavMode at
     * 'brand', so the bridge effect below would never re-run on its own.
     */
    const repaintNav = (next: string | null) => {
        navBaseColorRef.current = next;
        activateNavThemeBridge({ navBaseColor: next });
    };

    // The embed host's light/dark: the SDK's isDarkMode, then any UpdateTheme
    // switch. A ref, like navBaseColorRef: a switch is applied directly.
    const hostDarkModeRef = useRef(isDarkModeInEmbed);
    // Applies a switch; set by the main effect while it is active.
    const applyHostDarkModeRef = useRef<((isDark: boolean) => void) | null>(
        null,
    );

    // A new isDarkMode from the SDK replaces an earlier switch. Declared
    // before the main effect, so it runs first when isDarkModeInEmbed changes.
    useEffect(() => {
        hostDarkModeRef.current = isDarkModeInEmbed;
    }, [isDarkModeInEmbed]);

    useEmbedEventSubscription(
        IncomingEventType.UpdateTheme,
        (theme: UpdateThemeParams) => {
            const isDarkMode = theme?.isDarkMode;
            let error: string | undefined;
            if (typeof isDarkMode !== 'boolean') {
                error = 'UpdateTheme expects isDarkMode to be true or false.';
            } else if (isDarkMode && !radiantEnabled) {
                // Light needs nothing; only dark depends on the radiant theme.
                error = 'Dark mode is not available in this embed.';
            }
            if (error) {
                dispatchValidationError(error);
                return { error };
            }
            if (isDarkMode !== hostDarkModeRef.current) {
                hostDarkModeRef.current = isDarkMode;
                applyHostDarkModeRef.current?.(isDarkMode);
            }
            return { isDarkMode };
        },
        [radiantEnabled, dispatchValidationError],
    );

    // The persisted brand color (null when none / flag off). Recomputes when
    // themeType flips (i.e. when the style config finishes loading), so a
    // reload reliably re-applies the saved brand color.
    const persistedBrand = useMemo<string | null>(() => {
        if (!radiantEnabled) return null;
        const raw =
            customStylingService.getConfig()?.getRawAppPanelColor?.() || null;
        const resolved = resolveThemeMode(raw, true);
        return resolved.radiant && resolved.mode === 'brand'
            ? resolved.sourceHex ?? null
            : null;
        // themeType is the "config changed" proxy; customStylingService is stable.
    }, [radiantEnabled, themeType]);

    // Whether the persisted config wants the top nav painted with the literal
    // brand hex. getApplyAsTopNavColor() already folds in the migration default
    // for configs that predate the checkbox.
    const persistedApplyAsTopNav = useMemo<boolean>(() => {
        if (!radiantEnabled) return false;
        return (
            customStylingService.getConfig()?.getApplyAsTopNavColor?.() ?? false
        );
        // Same "config changed" proxy as persistedBrand above.
    }, [radiantEnabled, themeType]);

    // --- Main theming effect: initial apply + all listeners -----------------
    useEffect(() => {
        if (!radiantEnabled) {
            // Flag OFF: no mode, no brand. The nav-bridge effect below reacts
            // to the null nav mode and tears the bridge down.
            setRadiantNavMode(null);
            return undefined;
        }

        const userId = sessionService?.getUserGuid?.() ?? '';

        let mediaQuery: MediaQueryList | null = null;
        let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;
        const removeMediaListener = () => {
            if (mediaQuery && mediaListener) {
                mediaQuery.removeEventListener('change', mediaListener);
            }
            mediaQuery = null;
            mediaListener = null;
        };

        // Runtime-applied color wins over the (possibly stale) persisted one,
        // so an admin change made this session reapplies the right color on a
        // mode toggle without a reload.
        const activeBrandColor = () =>
            getCurrentSourceColor() || persistedBrand;

        let lastRequestedDark = false;
        let appliedBreakVars: string[] = [];

        // A precedence, not an OR: persistedApplyAsTopNav goes stale the
        // moment the admin saves, so a stale `true` would resurrect the
        // literal nav after the box was unchecked.
        const activeApplyAsTopNav = () =>
            getCurrentSourceColor()
                ? isNavColorLiteral()
                : persistedApplyAsTopNav;

        const applyForMode = (isDark: boolean) => {
            lastRequestedDark = isDark;
            if (isDark) {
                document.documentElement.setAttribute('data-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-theme');
            }

            const brand = activeBrandColor();
            const applyAsTopNavColor = activeApplyAsTopNav();
            if (brand) {
                appliedBreakVars = getCascadeBreakVars(
                    customStylingService.getConfig(),
                );
                try {
                    registerAppliedVars(
                        applyTheme(brand, {
                            isDark,
                            applyAsTopNavColor,
                            cascadeBreakVars: appliedBreakVars,
                        }),
                    );
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(
                        '[dynamic-theme] Failed to apply brand theme',
                        error,
                    );
                }
            }
            repaintNav(brand && applyAsTopNavColor ? brand : null);

            let navMode: 'brand' | 'dark' | 'light';
            if (brand) navMode = 'brand';
            else if (isDark) navMode = 'dark';
            else navMode = 'light';
            setRadiantNavMode(navMode);
        };

        const installMediaListenerIfSystem = (mode: UserThemeMode) => {
            if (mode !== 'system') return;
            mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            mediaListener = (e: MediaQueryListEvent) => applyForMode(e.matches);
            mediaQuery.addEventListener('change', mediaListener);
        };

        // Read when used, so an UpdateTheme switch is never stale here.
        const hostMode = (): UserThemeMode =>
            hostDarkModeRef.current ? 'dark' : 'light';
        const initialMode = isEmbedded
            ? hostMode()
            : getUserThemeModePref(userId);
        applyForMode(resolveEffectiveDark(initialMode));
        installMediaListenerIfSystem(initialMode);
        applyHostDarkModeRef.current = applyForMode;

        // Appearance dropdown changed the mode.
        const onUserThemeChange = (event: Event) => {
            if (isEmbedded) return;
            const { mode } = (event as CustomEvent).detail ?? {};
            removeMediaListener();
            applyForMode(resolveEffectiveDark(mode as UserThemeMode));
            installMediaListenerIfSystem(mode as UserThemeMode);
        };
        document.addEventListener('rd:user-theme-changed', onUserThemeChange);

        // Admin brand picker (cross-MFE). handleApply/handleClear inside the
        // listener do the applyTheme/clearTheme + embed registration; the nav
        // event keeps radiantNavMode in sync.
        const cleanupBridge = registerDynamicThemeListener({
            onNavModeChange: ({ active, mode, navBaseColor: navColor }) => {
                // Also carries the user's light/dark from the appearance
                // menu; an embed keeps the host's instead.
                const navMode =
                    isEmbedded && mode !== 'brand' ? hostMode() : mode;
                if (!active) {
                    // No repaint: the effect below tears the bridge down.
                    navBaseColorRef.current = null;
                    setRadiantNavMode(null);
                    return;
                }
                if (navColor === undefined) {
                    // A mode-only dispatcher (appearance menu, preference
                    // page) knows nothing about branding, so re-derive rather
                    // than erase. Runtime-only, NOT activeBrandColor(): a
                    // stale persistedBrand would resurrect a cleared brand.
                    const brand = getCurrentSourceColor();
                    repaintNav(brand && isNavColorLiteral() ? brand : null);
                    setRadiantNavMode(brand ? 'brand' : navMode || 'light');
                    return;
                }
                // An explicit value, null included: the picker knows.
                setRadiantNavMode(navMode || 'light');
                repaintNav(navColor);
            },
        });

        // The config is replaced during boot without themeType changing, so
        // the first apply can run before the chart fonts arrive.
        const unsubscribeConfig = subscribeToConfigChange(() => {
            const nextBreakVars = getCascadeBreakVars(
                customStylingService.getConfig(),
            );
            if (
                activeBrandColor() &&
                nextBreakVars.join() !== appliedBreakVars.join()
            ) {
                applyForMode(lastRequestedDark);
            }
        });

        return () => {
            document.removeEventListener(
                'rd:user-theme-changed',
                onUserThemeChange,
            );
            removeMediaListener();
            cleanupBridge();
            unsubscribeConfig();
            applyHostDarkModeRef.current = null;
        };
    }, [
        radiantEnabled,
        persistedBrand,
        persistedApplyAsTopNav,
        isEmbedded,
        isDarkModeInEmbed,
    ]);

    // --- Nav bridge reaction: pure function of the nav mode -----------------
    useEffect(() => {
        if (radiantNavMode) {
            activateNavThemeBridge({ navBaseColor: navBaseColorRef.current });
            // Signal radiant theme to surfaces that gate styling on it
            // (liveboard styling panel/header, color palette popover). Set on
            // <html> so body-portaled popovers inherit it too.
            document.documentElement.setAttribute('data-radiant-theme', '');
        } else {
            deactivateNavThemeBridge();
            document.documentElement.removeAttribute('data-radiant-theme');
        }
    }, [radiantNavMode]);

    const effectiveThemeType = useMemo(() => {
        if (!radiantNavMode) return leftNavThemeType;
        if (radiantNavMode === 'dark') return blinkConstants.themeType.DARK;
        return blinkConstants.themeType.TWO_TONE;
    }, [radiantNavMode, leftNavThemeType]);

    return { radiantNavMode, effectiveThemeType };
}
